import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { A11yModule, FocusKeyManager } from '@angular/cdk/a11y';
import { merge, Subject, Subscription } from 'rxjs';
import { filter, startWith, switchMap } from 'rxjs/operators';
import { TranslateDirective } from '@ngx-translate/core';

import { IconComponent } from '../icon/icon.component';
import { IdService } from '../../services/id.service';
import { PlatformService } from '../../services/platform.service';
import { KeyCode } from '../../domain/key-code.enum';

import { ScreenReaderVisibilityDirective } from '../../directives/screen-reader-visibility/screen-reader-visibility.directive';
import type { PositionY, PositionX } from '../overlay/overlay-origin.directive';

import type { Observable } from 'rxjs';
import type { FocusOrigin } from '@angular/cdk/a11y';
import type { AfterContentInit, OnDestroy, InputSignal, OutputEmitterRef } from '@angular/core';
import type { FlyoutMenuItemDirective } from './flyout-menu-item.directive';

export interface FlyoutMenuGlobalPosition {
  /** Override for the modal's top position. */
  top?: string;

  /** Override for the modal's bottom position. */
  bottom?: string;

  /** Override for the modal's left position. */
  left?: string;

  /** Override for the modal's right position. */
  right?: string;
}

export type FlyoutMenuCloseReason = void | 'keyboard' | 'select' | 'accessibility';

@Component({
  imports: [
    CommonModule,
    A11yModule,
    IconComponent,
    ScreenReaderVisibilityDirective,
    TranslateDirective
  ],
  selector: 'td-wb-flyout-menu',
  templateUrl: 'flyout-menu.component.html',
  styleUrls: ['flyout-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'tdWbFlyoutMenu'
})
export class FlyoutMenuComponent implements AfterContentInit, OnDestroy {
  private static readonly ID_PREFIX: string = 'td-wb-flyout-menu-aria';

  @ViewChild(TemplateRef) templateRef: TemplateRef<unknown>;

  readonly selectedKey: InputSignal<string | undefined> = input<string>(undefined, { alias: 'selectedKey' });

  readonly open: OutputEmitterRef<void> = output<void>();

  readonly close: OutputEmitterRef<FlyoutMenuCloseReason> = output<FlyoutMenuCloseReason>();

  parentMenu: FlyoutMenuComponent | undefined;
  menuId: string;
  menuOpened: boolean = false;

  overlayPosition: PositionX | PositionY = 'bottom';

  platformIOS: boolean = false;

  private items: Array<FlyoutMenuItemDirective> = [];
  private itemChanges: Subject<Array<FlyoutMenuItemDirective>> = new Subject<Array<FlyoutMenuItemDirective>>();
  private menuSelectSubscription: Subscription = Subscription.EMPTY;
  private tabSubscription: Subscription = Subscription.EMPTY;
  private keyManager: FocusKeyManager<FlyoutMenuItemDirective>;

  private readonly idService: IdService = inject(IdService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly platformService: PlatformService = inject(PlatformService);

  ngAfterContentInit(): void {
    this.initKeyManager();
    this.subscribeTab();
    this.subscribeMenuSelect();
    this.initFields();
  }

  ngOnDestroy(): void {
    this.itemChanges.complete();
    this.menuSelectSubscription.unsubscribe();
    this.tabSubscription.unsubscribe();
    this.keyManager.withHorizontalOrientation('ltr');
  }

  getMenuId(): string {
    return this.menuId;
  }

  /**
   * Get position classes based on overlay position
   */
  getPositionClass(): string {
    return `td-wb-flyout-menu__panel-${this.overlayPosition}`;
  }

  /** Handle a keyboard event from the menu,
   * delegating to the appropriate action.
   */
  handleKeydown(event: KeyboardEvent, keyCode: string): void {
    const manager = this.keyManager;

    switch (keyCode) {
      case KeyCode.ESC:
        this.close.emit('keyboard');
        break;
      case KeyCode.ARROW_LEFT:
        if (this.parentMenu !== undefined) {
          this.close.emit('keyboard');
        }
        break;
      case KeyCode.HOME:
      case KeyCode.END:
        if (!(event.altKey || event.ctrlKey || event.shiftKey || event.metaKey)) {
          keyCode === KeyCode.HOME ? manager.setFirstItemActive() : manager.setLastItemActive();
          event.preventDefault();
        }
        break;
      default:
        if (keyCode === KeyCode.ARROW_UP || keyCode === KeyCode.ARROW_DOWN) {
          manager.setFocusOrigin('keyboard');
        }
        manager.onKeydown(event);
    }
  }

  /**
   * Focus the first item in the menu or the selected item
   * mathing this selectedKey with the flyout-menu-item key.
   *
   * @param origin Action from which the focus originated. Used to set the correct styling.
   */
  focusFirstItemOrSelected(origin: FocusOrigin = 'program'): void {
    const selectedKeyValue = this.selectedKey();
    if (selectedKeyValue === undefined) {
      this.keyManager.setFocusOrigin(origin).setFirstItemActive();
    } else {
      const itemPosition = this.items.findIndex((el: FlyoutMenuItemDirective) => el.key() === selectedKeyValue);
      this.keyManager.setFocusOrigin(origin).setActiveItem(itemPosition === -1 ? 0 : itemPosition);
    }
  }

  /**
   * Registers a menu item with the menu.
   */
  addItem(item: FlyoutMenuItemDirective): void {
    if (this.items.indexOf(item) === -1) {
      this.items.push(item);
      this.itemChanges.next(this.items);
    }
  }

  /**
   * Removes an item from the menu.
   */
  removeItem(item: FlyoutMenuItemDirective): void {
    const index = this.items.indexOf(item);

    if (this.items.indexOf(item) > -1) {
      this.items.splice(index, 1);
      this.itemChanges.next(this.items);
    }
  }

  /**
   * Stream that emits whenever the clicked menu item changes.
   */
  itemClicked(): Observable<FlyoutMenuItemDirective> {
    return this.itemChanges.pipe(
      startWith(this.items),
      switchMap((items: Array<FlyoutMenuItemDirective>): Observable<FlyoutMenuItemDirective> => merge(...items.map((item: FlyoutMenuItemDirective): Observable<FlyoutMenuItemDirective> => item.clickedSubject)))
    );
  }

  closeButtonClick(): void {
    this.close.emit('accessibility');
  }

  setIsMenuOpen(isOpen: boolean): void {
    this.menuOpened = isOpen;
    this.changeDetectorRef.markForCheck();
  }

  private initFields(): void {
    this.platformIOS = this.platformService.isIOS();
    this.menuId = this.idService.generateNamespaceSequentialId(FlyoutMenuComponent.ID_PREFIX);
  }

  private subscribeMenuSelect(): void {
    this.menuSelectSubscription = this.itemClicked().pipe(filter((item: FlyoutMenuItemDirective): boolean => !item.triggersSubmenu()))
      .subscribe((): void => {
        this.close.emit('select');
      });
  }

  private subscribeTab(): void {
    this.tabSubscription = this.keyManager.tabOut.subscribe((): void => {
      this.close.emit('keyboard');
    });
  }

  private initKeyManager(): void {
    this.keyManager = new FocusKeyManager<FlyoutMenuItemDirective>(this.items).withWrap()
      .withVerticalOrientation()
      .withHorizontalOrientation('ltr');
  }
}
